import { useQueryClient } from "@tanstack/react-query";
import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { flushSync } from "react-dom";

import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons";

import {
	ButtonStandard,
	Checkbox,
	FormField,
	FormHandle,
	FormTextEditor,
	Icon,
	InputText,
	PYForm,
	Tooltip,
	toast,
	usePyForm,
} from "@performyard/py-components";

import { HasChangesBridge } from "../../../../components/PYFormBridges/HasChangesBridge";
import { setFieldServerError } from "../../../../components/PYFormBridges/setFieldServerError";
import CompetencyLevelManager from "./CompetencyLevelManager";

import {
	competenciesKeys,
	useCheckCompetencyName,
	useCreateCompetency,
	useUpdateCompetency,
} from "../../../../api/hooks/defs/competencies";
import useDebouncedValue from "../../../../hooks/useDebouncedValue";
import useNavigate from "../../../../hooks/useNavigate";
import useUnsavedChanges from "../../../../hooks/useUnsavedChanges";

import { checkCompetencyName } from "../../../../api/services/defs/competencies";

import {
	COMPETENCIES_LIST_PATH,
	CompetencyFormValues,
	MAX_COMPETENCY_DESCRIPTION_LENGTH,
	competencyFormSchema,
	toRequestPayload,
} from "./validationSchema";

const COMPANY_WIDE_TOOLTIP = "Makes this competency available to everyone across the organization, regardless of role.";
const NAME_COLLISION_MESSAGE = "Competency name already exists";

/**
 * PY-12513: `validate(true)` in py-components only touches top-level value
 * keys, so nested FormList children (`levels[N].name`) stay untouched and
 * their required-field errors never render. Figma node 109:24865 shows the
 * design intent — clicking "+ Add Level" is implicit intent to add a level,
 * and a subsequent blank submit should surface the level's required-field
 * error the same way a blank top-level required field does.
 *
 * The form's imperative ref (`FormHandle`) doesn't expose `setFieldMeta`, but
 * the full pyFormApi (which does) is available via the `usePyForm` context
 * inside the `<PYForm>`. This small bridge lives inside the form, grabs
 * `setFieldMeta` from context, and exposes a single `touchAllLevels` method
 * for the submit handler to call before `validate(true)`.
 */
interface LevelTouchBridgeHandle {
	touchAllLevels: () => void;
	setNameError: (message: string) => void;
}

const LevelTouchBridge = forwardRef<LevelTouchBridgeHandle>((_, ref) => {
	const pyForm = usePyForm();
	useImperativeHandle(
		ref,
		() => ({
			touchAllLevels: () => {
				// pyForm.state.values is a stale memoized snapshot; read the live array
				// via getFieldValue so newly-pushed levels are included.
				const levels = (pyForm.getFieldValue("levels") ?? []) as CompetencyFormValues["levels"];
				levels.forEach((_level, index) => {
					pyForm.setFieldMeta(`levels[${index}].name`, (prev: Record<string, unknown> | undefined) => ({
						...prev,
						isTouched: true,
					}));
					pyForm.setFieldMeta(
						`levels[${index}].description`,
						(prev: Record<string, unknown> | undefined) => ({
							...prev,
							isTouched: true,
						})
					);
				});
			},
			setNameError: (message: string) => setFieldServerError(pyForm, "name", message),
		}),
		[pyForm]
	);
	return null;
});
LevelTouchBridge.displayName = "LevelTouchBridge";

export interface CompetencyFormProps {
	mode: "create" | "edit";
	competencyId?: string;
	initialValues: CompetencyFormValues;
}

/**
 * Create/Edit competency form. Controlled by `mode` + `initialValues` from the
 * page wrapper. Handles:
 *   - Debounced server-side name uniqueness check (skipped in edit mode when
 *     the name is unchanged from initialValues).
 *   - Submit branching between create and update mutations.
 *   - Rank-stamping levels at the submit boundary via toRequestPayload.
 *   - Toast + navigate on success, setError on name-collision failures, toast
 *     on other failures.
 */
const CompetencyForm: React.FC<CompetencyFormProps> = ({ mode, competencyId, initialValues }) => {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const formRef = useRef<FormHandle<typeof competencyFormSchema>>(null);
	const levelTouchRef = useRef<LevelTouchBridgeHandle>(null);
	const { setIsUnsafe, setIsSafe } = useUnsavedChanges();

	// Track the name locally so the debounced name-availability check can prime
	// React Query's cache while the user types. The check has no UI of its own;
	// it exists only so the submit-time `ensureQueryData` lookup is a cache hit
	// in the common case (user pauses long enough for the debounce to settle).
	const [nameDraft, setNameDraft] = useState(initialValues.name);
	const debouncedName = useDebouncedValue(nameDraft.trim(), 500);
	const initialNameTrimmed = initialValues.name.trim();
	const shouldCheckName = debouncedName.length > 0 && debouncedName !== initialNameTrimmed;
	useCheckCompetencyName(shouldCheckName ? debouncedName : "", competencyId);

	const createMutation = useCreateCompetency();
	const updateMutation = useUpdateCompetency();
	const isSubmitting = createMutation.isPending || updateMutation.isPending;

	const handleSubmit = async ({ value }: { value: CompetencyFormValues; resetForm: () => void }) => {
		// Backstop for the cache-miss path (Enter key, or click before debounce).
		// staleTime avoids a redundant background refetch on cache hit.
		const trimmedName = value.name.trim();
		// Route name-collision errors through the bridge — it sets isTouched alongside serverError; PYForm's submit-handler setError only writes serverError, which FormField hides until isTouched is true.
		if (trimmedName.length > 0 && trimmedName !== initialNameTrimmed) {
			const result = await queryClient.ensureQueryData({
				queryKey: competenciesKeys.checkName(trimmedName, competencyId),
				queryFn: () => checkCompetencyName(trimmedName, competencyId),
				staleTime: 60_000,
			});
			if (!result.available) {
				levelTouchRef.current?.setNameError(NAME_COLLISION_MESSAGE);
				return;
			}
		}

		const payload = toRequestPayload(value);
		try {
			if (mode === "edit" && competencyId) {
				await updateMutation.mutateAsync({ id: competencyId, body: payload });
				toast.success("Changes saved");
			} else {
				await createMutation.mutateAsync(payload);
				toast.success("Competency created");
			}
			// flushSync so the blocker sees isUnsafe=false before navigate() runs.
			flushSync(() => setIsSafe());
			navigate(COMPETENCIES_LIST_PATH);
		} catch (error) {
			// Status code, not message string — the userResponse for ConflictError is generic.
			const err = error as { response?: { status?: number }; data?: { message?: string } };
			if (err.response?.status === 409) {
				levelTouchRef.current?.setNameError(NAME_COLLISION_MESSAGE);
			} else {
				toast.error(err.data?.message || "Something went wrong");
			}
		}
	};

	const primaryLabel = mode === "edit" ? "Save Changes" : "Create Competency";

	return (
		<PYForm
			ref={formRef}
			id="competency-form"
			schema={competencyFormSchema}
			defaultValues={initialValues}
			onSubmit={handleSubmit}
			validateOn={["onSubmit"]}
		>
			<LevelTouchBridge ref={levelTouchRef} />
			<HasChangesBridge onHasChanges={setIsUnsafe} />
			<div className="flex flex-col gap-4">
				<FormField<string>
					id="name"
					label="Competency Name"
					validators={{
						// Clear stale duplicate-name error once user types past the rejected value.
						onChange: ({ fieldApi }) => {
							if (fieldApi.state.meta.serverError) {
								fieldApi.state.meta.serverError = undefined;
							}
						},
					}}
					input={(field) => (
						<InputText
							id="name"
							value={field.value}
							onChange={(val) => {
								field.onChange(val);
								setNameDraft(val);
							}}
							onBlur={field.onBlur}
							placeholder="Your text here"
						/>
					)}
				/>

				{/* Description */}
				<FormTextEditor
					id="description"
					label="Description"
					characterLimit={MAX_COMPETENCY_DESCRIPTION_LENGTH}
					placeholder="Description"
					minHeight={60}
					maxHeight={120}
				/>

				{/* Company Wide */}
				<FormField<boolean>
					id="is_company_wide"
					input={(field) => (
						<div className="flex items-center gap-2 border-0 border-b border-solid border-neutral-2 pb-4">
							<Checkbox
								id="is_company_wide"
								checked={field.value}
								onCheckedChange={(checked) => field.onChange(checked as boolean)}
								aria-label="Company Wide Competency"
							/>
							<div className="flex items-center gap-0.5">
								<label
									htmlFor="is_company_wide"
									className="cursor-pointer text-paragraph-3 font-medium text-neutral-9"
								>
									Company Wide Competency
								</label>
								<Tooltip content={COMPANY_WIDE_TOOLTIP} placement="top">
									<Icon icon={faCircleInfo} size={12} color="neutral-7" />
								</Tooltip>
							</div>
						</div>
					)}
				/>

				{/* Levels */}
				<CompetencyLevelManager />
			</div>

			{/* Footer — full-width side-by-side buttons (Figma node 109:24865).
			    ButtonStandard already renders with `w-full` internally, so wrapping
			    each in a `flex-1` cell fills the row edge-to-edge with an 8px gap. */}
			<div className="mt-6 flex gap-2">
				<div className="flex-1">
					<ButtonStandard
						variant="outline"
						color="neutral"
						size="large"
						onClick={() => navigate(COMPETENCIES_LIST_PATH)}
					>
						Cancel
					</ButtonStandard>
				</div>
				<div className="flex-1">
					<ButtonStandard
						variant="solid"
						color="primary"
						size="large"
						type="button"
						disabled={isSubmitting}
						onClick={async () => {
							const trimmedName = nameDraft.trim();
							if (trimmedName.length > 0 && trimmedName !== initialNameTrimmed) {
								const cached = queryClient.getQueryData<{ available: boolean }>(
									competenciesKeys.checkName(trimmedName, competencyId)
								);
								if (cached?.available === false) {
									levelTouchRef.current?.setNameError(NAME_COLLISION_MESSAGE);
									return;
								}
							}

							levelTouchRef.current?.touchAllLevels();
							const isValid = await formRef.current?.validate(true);
							if (isValid) {
								await formRef.current?.submit();
							}
						}}
					>
						{primaryLabel}
					</ButtonStandard>
				</div>
			</div>
		</PYForm>
	);
};

export default CompetencyForm;
